import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar, { DatesSetArg, EventContentArg } from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import { Checkbox, FormControl, InputLabel, ListItemText, ListSubheader, MenuItem, OutlinedInput, Select, Input } from '@mui/material'
import { flatten } from 'lodash'
import moment from 'moment'
import queryString from 'query-string'
import React from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import styled from 'styled-components'

import { getRentablesUnavailabilities, getLocations, IFetchLocationsResponse } from '../../common/requests'
import User from '../../dtos/User'
import { ICalendarRange, IRentableUnavailability } from './types'
import { getOptions, groupBookedTimeSlots, ILocationOptions } from './utils'
import * as yup from 'yup'
import ReactDOM from 'react-dom'
import GoogleTagManager from '../../common/components/GoogleTagManager'

const RentableUnavailabilityContainer = styled.div`
    padding: 40px;

    .fc-button {
        border-radius: 0 !important;
        border: none !important;
        background-color: crimson !important;
        border-color: crimson !important;
        box-shadow: none !important;
    }

    .fc-button[disabled] {
        background-color: grey !important;
    }

    .fc-col-header  {
        text-transform: capitalize;
    }

    .fc-timegrid-event-harness.fc-timegrid-event-harness-inset a {
        border-radius: 0;
        pointer-events: none;
    }

    .fc-rentablesDropdown-button {
        width: 200px;
        padding: 0;
        background-color: white !important;
        text-align: initial;

        &:hover {
          background-color: white !important;
        }
    }

    .fc-button.fc-button:not(.fc-rentablesDropdown-button, .fc-goToDate-button) {
        background-color: ${({ theme }) => theme.primary} !important;
    }

    .fc-button:hover:not(.fc-rentablesDropdown-button, .fc-goToDate-button),
    .fc-button.fc-button-active:not(.fc-rentablesDropdown-button, .fc-goToDate-button) {
         filter: brightness(80%);
    }

    .fc-rentablesDropdown-button {
        width: 200px;
        padding: 0;
        background-color: white !important;
        text-align: initial;

        &:hover {
            background-color: white !important;
        }
    }

    .fc-goToDate-button {
     background-color: white !important;
          &:hover {
          background-color: white !important;
        }
    }
`

const ROW_TITLE_WIDTH = 41
const MIN_DAY_WIDTH = 400;
const TIME_REGEX = /^(?:[01]?\d|2[0123]):(?:[012345]\d):(?:[012345]\d)$/
const DATE_REGEX = /^(20)\d\d-(0[1-9]|1[012])-(0[1-9]|[12]\d|3[01])$/
const DISPLAY_UP_TO_REGEX = /^\d+(d|day|days|w|week|weeks|M|month|months|y|year|years)$/
const CalendarWrapper = styled.div<{ amountOfDays: number }>`
    @media only screen and (max-width: 767px) {
        min-width: ${props => `${ROW_TITLE_WIDTH + MIN_DAY_WIDTH * props.amountOfDays}px`}
    }
`
const ErrorMessage = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
`
const ERROR_TO_TEXT = {
    hiddenDays: "general.urlParametersErrors.hiddenDays",
    startingAt: "general.urlParametersErrors.startEndTime",
    endingAt: "general.urlParametersErrors.startEndTime",
    startDate: "general.urlParametersErrors.calendarDate",
    endDate: "general.urlParametersErrors.calendarDate",
    displayUpTo: "general.urlParametersErrors.displayUpTo",
    selectedRentables: "general.urlParametersErrors.selectedRentables",
}

interface ICalendarProps extends WithTranslation, RouteComponentProps {
    organizationId: number
    organizationSlug: string | undefined
    shopUrl: string | undefined
    language: string
    gtag: string | null
}

interface ICalendarState {
    options: ILocationOptions[]
    weekendsVisible: boolean
    selectedRentables: string[]
    selectionableRentables: string[],
    isLoading: boolean
    isLoggedIn: boolean
    user: Partial<User> | null
    hiddenDays: number[]
    canCloseModal: boolean
    showWarningModal: boolean
    startTime: string
    endTime: string
    facilities?: IFetchLocationsResponse
    availabilities?: IRentableUnavailability[]
    startDate: string
    endDate: string
    calendarRange: ICalendarRange
    displayUpTo: string | null
    hideRentablesFilter: boolean
    labelText: string
    errors?: (keyof typeof ERROR_TO_TEXT)[]
}

function renderEventContent(eventContent: EventContentArg) {
    return (
        <div>
            <i>{eventContent.event.title}</i>
        </div>
    )
}

export class RentableUnavailability extends React.Component<ICalendarProps, ICalendarState> {
    state: ICalendarState = {
        weekendsVisible: true,
        selectedRentables: [],
        selectionableRentables: [],
        options: [],
        user: null,
        isLoading: true,
        isLoggedIn: false,
        hiddenDays: [],
        canCloseModal: true,
        showWarningModal: false,
        startTime: '06:00:00',
        endTime: '23:00:00',
        startDate: moment().startOf('week').toISOString(),
        endDate: moment().endOf('week').toISOString(),
        displayUpTo: null,
        hideRentablesFilter: false,
        calendarRange: {} as ICalendarRange,
        labelText: '',
        errors: undefined
    }

    calendarRef = React.createRef<FullCalendar>();

    async componentDidMount() {
        const urlParams = queryString.parse(this.props.location.search) as Record<string, string | string[] | undefined>;
        // `${path}` is interpolated by yup at runtime, not a JS template literal.
        /* eslint-disable no-template-curly-in-string */
        const schema = yup.object({
            selectedRentables: yup.string()
                .required('${path}'),
            hiddenDays: yup.string()
                .matches(/^(\d+)(,(\d+))*$/, '${path}'),
            startingAt: yup.string()
                .matches(TIME_REGEX, '${path}'),
            endingAt: yup.string()
                .matches(TIME_REGEX, '${path}'),
            startDate: yup.string()
                .matches(DATE_REGEX, '${path}'),
            endDate: yup.string()
                .matches(DATE_REGEX, '${path}'),
            displayUpTo: yup.string()
                .matches(DISPLAY_UP_TO_REGEX, '${path}'),
        })
        /* eslint-enable no-template-curly-in-string */

        if (!await schema.isValid(urlParams)) {
            try {
                await schema.validate(urlParams, { abortEarly: false })
            } catch (e) {
                this.setState({
                    errors: e.errors
                })
            }
            return
        }

        const facilities = await getLocations({
            organizationId: this.props.organizationId,
            selectionableRentables: ((urlParams.selectedRentables ?? "") as string).split(',').filter(x => x),
        })

        const hideRentablesFilter = Boolean(urlParams.hideRentablesFilter)
        const options = getOptions(facilities.locations || [])
        const selectionableRentables = urlParams.selectedRentables ? (urlParams.selectedRentables as string).split(',').map(x => String(x)) : []

        this.setState({
            facilities,
            options,
            hiddenDays: urlParams.hiddenDays ? (urlParams.hiddenDays as string).split(',').map(x => Number(x)) : [],
            selectionableRentables,
            startTime: urlParams.startingAt
                ? String(urlParams.startingAt)
                : this.state.startTime,
            endTime: urlParams.endingAt
                ? String(urlParams.endingAt)
                : this.state.endTime,
            calendarRange: this.getCalendarRange(
                urlParams.startDate ? String(urlParams.startDate) : null,
                urlParams.endDate ? String(urlParams.endDate) : null,
                urlParams.displayUpTo ? String(urlParams.displayUpTo) : null
            ),
            hideRentablesFilter: hideRentablesFilter,
            selectedRentables: options
                .map(x => x.options)
                .flat()
                .filter(option => selectionableRentables.includes(option.slug))
                .map(x => `${x.id}-${x.type}`),
            labelText: String(urlParams.labelText || '')
        })
    }

    handleChange = (
        event: any,
    ) => {
        this.setState({
            selectedRentables: typeof event.target.value === 'string'
                ? event.target.value.split(',')
                : event.target.value,
        }, () => {
            this.fetchAvailabilities({
                startStr: this.state.startDate,
                endStr: this.state.endDate
            })
        });
    }

    getCalendarRange = (
        startDate: string | null,
        endDate: string | null,
        displayUpTo: string | null
    ): ICalendarRange => {
        const today = moment().format('YYYY-MM-DD');
        const calendarStartDate = startDate && startDate > today
            ? startDate
            : today;

        if (displayUpTo) {
            const displayUpToArray = (displayUpTo ?? "" as string).match(/[a-z]+|\d+/g);
            const displayUpToAmount = displayUpToArray?.[0] as moment.unitOfTime.DurationConstructor
            const displayUpToUnit = displayUpToArray?.[1] as moment.unitOfTime.DurationConstructor;
            return {
                start: calendarStartDate,
                end: moment(calendarStartDate)
                    .add(displayUpToAmount, displayUpToUnit)
                    .toISOString()
            }
        }

        if (endDate && endDate > calendarStartDate) {
            return {
                start: calendarStartDate,
                end: moment(endDate).add(1, 'day').toISOString()
            }
        }

        return {
            start: calendarStartDate,
            end: undefined
        }
    }

    loadEvents = () => flatten(
        groupBookedTimeSlots(
            this.state.availabilities || [],
            this.state.selectedRentables
        ).map(x => {
            return {
                id: '1',
                title: this.props.t('unavailability'),
                color: '#777b7e',
                start: moment(x.date)
                    .hour(moment(x.start_time, 'HH:mm').hour())
                    .minutes(moment(x.start_time, 'HH:mm').minutes())
                    .seconds(0)
                    .toISOString(),
                end: moment(x.date)
                    .hour(moment(x.end_time, 'HH:mm').hour())
                    .minutes(moment(x.end_time, 'HH:mm').minutes())
                    .seconds(0)
                    .toISOString(),
            }
        })
    )

    handleDateChange = (event: any,) => {
        const calendarApi = this.calendarRef.current?.getApi();
        calendarApi?.gotoDate(moment(event.target.value).startOf('week').toISOString());
    }

    fetchAvailabilities = async ({ startStr, endStr }: Pick<DatesSetArg, 'startStr' | 'endStr'>) => {
        if (this.state.startDate !== startStr && this.state.endDate !== endStr) {
            this.setState({
                startDate: startStr,
                endDate: endStr
            })
        }

        await getRentablesUnavailabilities({
            organizationId: this.props.organizationId,
            units: this.state.selectedRentables.map(x => x.split('-')).filter(([, type]) => type === 'unit').map(([id]) => id),
            facilities: this.state.selectedRentables.map(x => x.split('-')).filter(([, type]) => type === 'facility').map(([id]) => id),
            //TODO INTERSECT TO DO NOT OVERFETCH DISABLED DAYS NOT IN CALENDAR RANGE
            startDate: moment(startStr).toISOString(),
            endDate: moment(endStr).toISOString(),
        })
            .then(availabilities => {
                this.setState({
                    availabilities
                })
            })
    }

    render() {
        if (Number(this.state.errors?.length) > 0) {
            return (
                <ErrorMessage>
                    <h3>
                        {this.props.t('general.invalidParameters')}
                    </h3>
                    <div>
                        {this.state.errors?.map(x => <div key={x}>{this.props.t(ERROR_TO_TEXT[x], { path: x })}</div>)}
                    </div>
                </ErrorMessage>
            )
        }

        const label = this.state.labelText || this.props.t('general.select');

        const rentablesDropdownButton = document.querySelector('.fc-rentablesDropdown-button')
        const goToDateButton = document.querySelector('.fc-goToDate-button')

        return (
            <RentableUnavailabilityContainer>
                <GoogleTagManager gtag={this.props.gtag} />
                {
                    !this.state.hideRentablesFilter
                    && rentablesDropdownButton
                    && ReactDOM.createPortal(
                        <FormControl
                            sx={{
                                width: '200px'
                            }}
                        >
                            <InputLabel id="facilities-and-units-label">{label}</InputLabel>
                            <Select
                                labelId="facilities-and-units-label"
                                id="facilities-and-units"
                                multiple
                                value={this.state.selectedRentables}
                                onChange={this.handleChange}
                                input={<OutlinedInput label={label} />}
                                renderValue={(selected: string[]) => {
                                    return selected.map(x => flatten(this.state.options.map(x => x.options)).find(y => `${y.id}-${y.type}` === x)?.name).join(', ')
                                }}
                                sx={{
                                    borderRadius: 0
                                }}
                            >
                                {
                                    this.state.options.map(({ id, name, options }) => {
                                        return [
                                            <ListSubheader sx={{ fontWeight: 'bold' }}>{name}</ListSubheader>,
                                            ...options.map(x => {
                                                const value = `${x.id}-${x.type}`
                                                const isChecked = this.state.selectedRentables.indexOf(value) > -1
                                                const isDisabled = isChecked && this.state.selectedRentables.length === 1
                                                if (this.state.selectionableRentables.indexOf(x.slug) > -1) {
                                                    return (
                                                        <MenuItem
                                                            key={value}
                                                            value={value}
                                                            style={{
                                                                paddingLeft: x.type === 'unit' ? 60 : 20
                                                            }}
                                                            disabled={isDisabled}
                                                        >
                                                            <Checkbox
                                                                checked={isChecked}
                                                                disabled={isDisabled}
                                                            />
                                                            <ListItemText primary={x.name} />
                                                        </MenuItem>
                                                    )
                                                }
                                                return (
                                                    <ListSubheader
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            paddingLeft: 5,
                                                        }}>
                                                        {x.name}
                                                    </ListSubheader>
                                                )
                                            })
                                        ]
                                    })
                                }
                            </Select>
                        </FormControl>,
                        rentablesDropdownButton
                    )
                }
                {
                    goToDateButton && ReactDOM.createPortal(
                        <FormControl>
                            <Input
                                type="date"
                                value={moment(this.state.startDate).format("YYYY-MM-DD")}
                                inputProps={{
                                    min: moment(this.state.calendarRange.start).format("YYYY-MM-DD"),
                                    max: moment(this.state.calendarRange.end || undefined).subtract(1, 'day').format("YYYY-MM-DD")
                                }}
                                onChange={this.handleDateChange}

                            />
                        </FormControl>,
                        goToDateButton
                    )
                }
                {
                    <CalendarWrapper amountOfDays={7 - this.state.hiddenDays.length}>
                        <FullCalendar
                            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                            customButtons={{
                                // NOTE: creating empty button which placeholds space for the dropdown
                                // dropdown injected using the react portal
                                rentablesDropdown: {
                                    text: ''
                                },
                                goToDate: {
                                    text: ''
                                },
                            }}
                            headerToolbar={{
                                center: 'title',
                                left: 'prev,next,today,goToDate',
                                right: 'rentablesDropdown'
                            }}
                            buttonText={{
                                today: this.props.t('today')
                            }}
                            firstDay={1}
                            hiddenDays={this.state.hiddenDays}
                            slotMinTime={this.state.startTime}
                            slotMaxTime={this.state.endTime}
                            initialView='timeGridWeek'
                            allDaySlot={false}
                            locale={this.props.language}
                            height="auto"
                            events={this.loadEvents()}
                            validRange={this.state.calendarRange}
                            datesSet={this.fetchAvailabilities}
                            eventContent={renderEventContent}
                            ref={this.calendarRef}
                        />
                    </CalendarWrapper>
                }
            </RentableUnavailabilityContainer>
        )
    }
}

export default withRouter(withTranslation()(RentableUnavailability));
